import '../../i18n/autoTranslate';
import React, { useState } from "react";
import { Lock, Trash2, AlertTriangle } from "lucide-react";
import { Button } from "../../../components/ui/shadcn/button";
import { Input } from "../../../components/ui/shadcn/input";
import { Label } from "../../../components/ui/shadcn/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/shadcn/dialog";

export default function AccountSettings({ embedded = false } = {}) {
  const [accountData, setAccountData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAccountData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    e.preventDefault();
    if (accountData.newPassword !== accountData.confirmPassword) {
      alert("New password and confirm password do not match.");
      return;
    }
    // Add logic to update password (e.g., API call)
    console.log("Password change requested:", accountData);
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      // Add logic to delete account (e.g., API call)
      console.log("Account deletion requested");
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      console.log("Account deleted successfully");
      // Redirect or show success message
    } catch (error) {
      console.error("Error deleting account:", error);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  return (
    <div className="space-y-6">
      {!embedded && (
        <>
          <h2 className="text-2xl font-semibold tracking-tight text-white">Account Settings</h2>
          <p className="text-gray-200/70 text-sm sm:text-base">Manage your account security and preferences.</p>
        </>
      )}
      <div className="space-y-6">
        {/* Change Password */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">Change Password</h3>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-200/80">Current Password</Label>
              <Input
                type="password"
                name="currentPassword"
                value={accountData.currentPassword}
                onChange={handleInputChange}
                placeholder="Enter current password"
                className="text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-200/80">New Password</Label>
              <Input
                type="password"
                name="newPassword"
                value={accountData.newPassword}
                onChange={handleInputChange}
                placeholder="Enter new password"
                className="text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-200/80">Confirm New Password</Label>
              <Input
                type="password"
                name="confirmPassword"
                value={accountData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Confirm new password"
                className="text-white"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-11 bg-gradient-to-br from-[#8B7CFF] to-[#6C63FF] text-white hover:shadow-[0_0_50px_rgba(108,99,255,0.35)]"
            >
              <Lock className="h-4 w-4" />
              Update Password
            </Button>
          </form>
        </div>

        {/* Delete Account */}
        <div className="border-t border-white/10 pt-6">
          <h3 className="text-lg font-semibold text-white">Delete Account</h3>
          <p className="text-gray-200/70 text-sm sm:text-base mt-2">
            Permanently delete your account and all associated data.
          </p>
          <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
            <DialogTrigger asChild>
              <Button variant="destructive" className="w-full h-11 mt-4">
                <Trash2 className="h-4 w-4" />
                Delete Account
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0B0B12] border-white/10 text-white">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-white">
                  <AlertTriangle className="h-5 w-5 text-red-400" />
                  Delete Account
                </DialogTitle>
                <DialogDescription className="text-gray-200/70">
                  This action cannot be undone. All your data will be permanently removed.
                </DialogDescription>
              </DialogHeader>
              <div className="text-sm text-gray-200/80">
                Proceed only if you’re sure you want to permanently delete your account.
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  className="border-white/10 bg-white/5 text-white hover:bg-white/10"
                  disabled={isDeleting}
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </Button>
                <Button variant="destructive" disabled={isDeleting} onClick={handleDeleteAccount}>
                  {isDeleting ? "Deleting..." : "Delete Account"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}
